import React, { FunctionComponent, useCallback } from "react";

import { useTranslation } from "react-i18next";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import { FIND_PERSON_COMPANY_CREDITS } from "@signalist/shared-types";

import { Icon } from "@/components/Icon/Icon";
import { Information } from "@/components/Information/Information";

import { useGetScoreCompanyCategoriesQuery } from "@/pages/Private/redux/list/list.api";

import { EnrichmentFilterConfig } from "../ListEditDetails";
import { List } from "../../../schema/list";
import { DefineIcp } from "./DefineIcp";
import { SearchProfilePresetBar } from "./searchProfilePreset/SearchProfilePresetBar";

interface DefineIcpsProps {
	list: List;
	titleFilters?: EnrichmentFilterConfig;
	setTitleFilters: React.Dispatch<React.SetStateAction<EnrichmentFilterConfig | undefined>>;
	maxPerCompany?: number;
	setMaxPerCompany: (maxPerCompany: number) => void;
	hasErrors: boolean;
	/** A tracker list finds exactly one person per company and shows its own price (PREC-4399). */
	hidePeoplePerCompany?: boolean;
	/** "Only enrich companies, find people later" — nothing is charged while the list runs (PREC-4400). */
	companyOnly?: boolean;
}

export const DefineIcps: FunctionComponent<DefineIcpsProps> = ({
	list,
	titleFilters,
	setTitleFilters,
	maxPerCompany,
	setMaxPerCompany,
	hasErrors,
	hidePeoplePerCompany = false,
	companyOnly = false,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`defineIcps.${key}`);

	useGetScoreCompanyCategoriesQuery(list.id, { skip: !list.id });

	const currentValue = maxPerCompany ?? 3;

	const handleDecrement = useCallback(() => {
		const newValue = Math.max(1, currentValue - 1);

		setMaxPerCompany(newValue);
	}, [currentValue, setMaxPerCompany]);

	const handleIncrement = useCallback(() => {
		const newValue = Math.min(3, currentValue + 1);

		setMaxPerCompany(newValue);
	}, [currentValue, setMaxPerCompany]);

	return (
		<div className="w-full">
			<SearchProfilePresetBar
				maxPerCompany={maxPerCompany}
				setMaxPerCompany={setMaxPerCompany}
				setTitleFilters={setTitleFilters}
				titleFilters={titleFilters}
			/>
			<div className="text-md mb-3 font-bold">{ts("findPeopleByJobTitles")}</div>
			<div className="text-sm text-text-meta mb-10 flex gap-1">
				{ts("upTo3searches")}
				<Information information={ts("upTo3searchesInfo")} />
			</div>
			<DefineIcp
				description={ts("firstSearchInfo")}
				error={hasErrors}
				list={list}
				prio="first"
				setTitleFilters={setTitleFilters}
				title={ts("firstSearch")}
				titleFilters={titleFilters}
			/>
			<DefineIcp
				description={ts("secondSearchInfo")}
				error={hasErrors}
				list={list}
				prio="second"
				setTitleFilters={setTitleFilters}
				title={ts("secondSearch")}
				titleFilters={titleFilters}
			/>
			<DefineIcp
				description={ts("thirdSearchInfo")}
				error={hasErrors}
				list={list}
				prio="third"
				setTitleFilters={setTitleFilters}
				title={ts("thirdSearch")}
				titleFilters={titleFilters}
			/>

			{/* People per company section */}
			{!hidePeoplePerCompany && (
				<div className="bg-white border border-border-light rounded-lg overflow-hidden mb-5">
					<div className="px-5 py-4 border-b border-border-light">
						<div className="text-[15px] font-semibold text-text">{ts("peoplePerCompany")}</div>
						<div className="text-xs text-gray-700 mt-0.5">{ts("peoplePerCompanySubtitle")}</div>
					</div>
					<div className="p-5 flex items-center gap-6 flex-wrap">
						{/* Stepper control */}
						<div className="flex items-center gap-2">
							<button
								className="w-7 h-7 rounded border border-border-light bg-white flex items-center justify-center text-base text-gray-700 hover:border-brand-light hover:text-brand-light transition-colors cursor-pointer"
								data-testid="people-count-decrement"
								type="button"
								onClick={handleDecrement}
							>
								−
							</button>
							<div
								className="w-10 text-center text-sm font-semibold text-text"
								data-testid="people-count-value"
							>
								{currentValue}
							</div>
							<button
								className="w-7 h-7 rounded border border-border-light bg-white flex items-center justify-center text-base text-gray-700 hover:border-brand-light hover:text-brand-light transition-colors cursor-pointer"
								data-testid="people-count-increment"
								type="button"
								onClick={handleIncrement}
							>
								+
							</button>
						</div>

						{/* Credit info box */}
						<div className="flex-1 flex items-start gap-2.5 p-3 bg-brand-white border border-border-light rounded-lg text-xs text-gray-700 leading-relaxed">
							<Icon className="w-4 h-4 text-brand-light flex-shrink-0 mt-0.5" icon={faInfoCircle} />
							{companyOnly ? (
								<div data-testid="credit-usage-company-only">
									<span className="font-semibold text-text">{ts("creditUsageLabel")}</span> nothing
									is charged while the list runs. When you run Find person on selected companies:{" "}
									{FIND_PERSON_COMPANY_CREDITS} credit per company searched (regardless of whether a
									person is found). Each additional person found beyond the first costs{" "}
									{FIND_PERSON_COMPANY_CREDITS} more credit.
									<br />
									<span className="text-text-disabled">
										Example: finding 2 people at one company = 2 credits. Finding 0 or 1 = 1 credit.
									</span>
								</div>
							) : (
								<div>
									<span className="font-semibold text-text">{ts("creditUsageLabel")}</span>{" "}
									{t("defineIcps.creditUsageDescription", { credits: FIND_PERSON_COMPANY_CREDITS })}
									<br />
									<span className="text-text-disabled">{ts("creditUsageExample")}</span>
								</div>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};
